import { ArrowRight, MonitorUp } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { PageBody, PageHeader } from '@/components/layout/page-header';
import { Callout } from '@/components/patterns/callout';
import { DataTable, TabelaPainel, type Coluna } from '@/components/patterns/data-table';
import { PermissionButton } from '@/components/patterns/permission-button';
import { SearchSelect } from '@/components/patterns/search-select';
import { EstadoErro, EstadoVazio } from '@/components/patterns/states';
import { StatusBuildBadge } from '@/components/patterns/status-badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field } from '@/components/ui/field';
import { Skeleton } from '@/components/ui/skeleton';
import { dataSource } from '@/data';
import type { Painel, PublicacaoPainel } from '@/data/types';
import { useEventos } from '@/hooks/use-eventos';
import { useResource } from '@/hooks/use-resource';
import { useSessao } from '@/hooks/use-sessao';
import { elegivelParaPainel, podePublicarNoPainel } from '@/lib/capabilities';
import { compararVersao, formatarData, formatarDataHora, formatarRelativo } from '@/lib/format';
import { DownloadLinks } from '@/features/versao/download-links';
import { PublicarPainelDialog } from '@/features/versao/acoes-build';

/**
 * Painel do cliente — a "versão estável recomendada" que os clientes veem no
 * painel e no PAN. Mostra a atual, desde quando, o histórico de trocas, e
 * permite publicar outra versão elegível (homologada, branch v2.*) para quem
 * tem a capability `painel`.
 */
export function PainelPage() {
  const { rolesCarregando } = useSessao();
  const painel = useResource((signal) => dataSource.obterPainel(signal), []);
  useEventos((evento) => {
    if (evento.tipo !== 'painel') painel.atualizarEmSilencio();
  });

  return (
    <PageBody>
      <PageHeader
        eyebrow="Compilações"
        titulo="Painel do cliente"
        descricao="A versão publicada aqui é a versão estável recomendada aos clientes. Só versões homologadas de branch v2.* podem ser publicadas."
      />

      {painel.estado === 'carregando' && !painel.dados ? (
        <div className="grid gap-4 lg:grid-cols-[3fr_2fr]" role="status" aria-label="Carregando o painel">
          <Skeleton className="h-56 rounded-card" />
          <Skeleton className="h-56 rounded-card" />
          <Skeleton className="h-64 rounded-card lg:col-span-2" />
        </div>
      ) : painel.estado === 'erro' || !painel.dados ? (
        <Card>
          <EstadoErro erro={painel.erro} aoTentarDeNovo={painel.recarregar} />
        </Card>
      ) : (
        <>
          <div className="grid gap-4 lg:grid-cols-[3fr_2fr]">
            <VersaoAtual painel={painel.dados} />
            {rolesCarregando ? (
              <div role="status" aria-label="Verificando suas permissões">
                <Skeleton className="h-56 rounded-card" />
              </div>
            ) : (
              <Publicar painel={painel.dados} aoPublicar={painel.definir} />
            )}
          </div>
          <Historico historico={painel.dados.historico} />
        </>
      )}
    </PageBody>
  );
}

function VersaoAtual({ painel }: { painel: Painel }) {
  return (
    <Card as="section" destacado aria-labelledby="painel-atual">
      <CardHeader destacado>
        <MonitorUp className="size-4 text-primary" aria-hidden="true" />
        <CardTitle id="painel-atual">Versão atual no painel</CardTitle>
      </CardHeader>
      <CardContent>
        {painel.versao_atual ? (
          <>
            <div className="flex flex-col gap-1">
              <Link
                to={`/versoes/${encodeURIComponent(painel.versao_atual)}`}
                className="w-fit font-mono text-page-title tabular-nums text-on-surface underline-offset-4 hover:text-primary hover:underline"
              >
                {painel.versao_atual}
              </Link>
              {painel.atualizado_em ? (
                <p className="text-body text-on-surface-muted">
                  No painel desde{' '}
                  <time dateTime={painel.atualizado_em} title={formatarDataHora(painel.atualizado_em)}>
                    {formatarData(painel.atualizado_em)} ({formatarRelativo(painel.atualizado_em)})
                  </time>
                </p>
              ) : null}
            </div>
            {painel.build ? (
              <div className="flex flex-col gap-3 border-t border-border-subtle pt-4">
                <p className="flex flex-wrap items-center gap-2 text-cell text-on-surface-muted">
                  <StatusBuildBadge status={painel.build.status} />
                  <span className="font-mono text-on-surface">{painel.build.branch}</span>·
                  <span>ramo {painel.build.ramo}</span>·<span>{painel.build.total_artefatos} artefatos</span>·
                  <span>publicada em {formatarDataHora(painel.build.publicado_em)}</span>
                </p>
                <DownloadLinks links={painel.build.links} versao={painel.build.versao} />
              </div>
            ) : (
              <Callout tom="alerta">
                A versão {painel.versao_atual} não corresponde a nenhum build publicado no Compilações (veio de antes
                da migração do WordPress, ou foi gravada direto no painel).
              </Callout>
            )}
          </>
        ) : (
          <EstadoVazio titulo="Nenhuma versão no painel" descricao="Publique uma versão homologada para os clientes verem." />
        )}
      </CardContent>
    </Card>
  );
}

function Publicar({ painel, aoPublicar }: { painel: Painel; aoPublicar: (painel: Painel) => void }) {
  const { roles } = useSessao();
  const permissao = podePublicarNoPainel(roles);
  const [versao, setVersao] = React.useState<string | null>(null);
  const [confirmando, setConfirmando] = React.useState(false);

  // Homologadas, mais recentes primeiro; o filtro de branch/formato espelha
  // a validação do `POST /v1/painel` (a API ainda é quem decide).
  const homologadas = useResource(
    (signal) => dataSource.listarBuilds({ status: ['homologado'], limit: 100 }, null, signal),
    [painel.versao_atual],
  );
  const elegiveis = (homologadas.dados?.items ?? [])
    .filter(elegivelParaPainel)
    .filter((item) => item.versao !== painel.versao_atual)
    .sort((a, b) => compararVersao(b.versao, a.versao));

  const atualAnterior = painel.versao_atual;

  return (
    <Card as="section" aria-labelledby="painel-publicar">
      <CardHeader>
        <CardTitle id="painel-publicar">Publicar outra versão</CardTitle>
      </CardHeader>
      <CardContent>
        {homologadas.estado === 'erro' ? (
          <Callout tom="erro">Não foi possível listar as versões homologadas: {homologadas.erro?.message}</Callout>
        ) : (
          <Field
            id="painel-versao"
            label="Versão elegível"
            apoio={
              homologadas.estado === 'carregando'
                ? 'Carregando versões homologadas…'
                : elegiveis.length === 0
                  ? 'Nenhuma versão homologada de branch v2.* disponível além da atual.'
                  : `${elegiveis.length} versões homologadas de branch v2.*.`
            }
          >
            {(props) => (
              <SearchSelect
                id={props.id}
                describedBy={props['aria-describedby']}
                valor={versao}
                aoMudar={setVersao}
                disabled={homologadas.estado !== 'pronto' || elegiveis.length === 0}
                placeholder="Escolha a versão"
                placeholderBusca="Buscar versão…"
                opcoes={elegiveis.map((item) => ({
                  valor: item.versao,
                  rotulo: item.versao,
                  detalhe: `${item.branch} · ${formatarData(item.publicado_em)}`,
                }))}
              />
            )}
          </Field>
        )}

        {versao ? (
          <p className="flex flex-wrap items-center gap-2 text-body">
            <span className="font-mono text-on-surface-muted">{atualAnterior ?? 'Nenhuma'}</span>
            <ArrowRight className="size-4 text-icon" aria-label="passa a ser" />
            <span className="font-mono font-bold text-primary">{versao}</span>
          </p>
        ) : null}

        {/* Sem capability: cadeado + motivo. Com capability e sem versão
            escolhida: só desabilitado — não é falta de permissão. */}
        <PermissionButton
          permitido={permissao.permitido}
          motivo={permissao.motivo}
          variant="primary"
          className="w-fit"
          disabled={!versao}
          title={!versao ? 'Escolha uma versão elegível para publicar.' : undefined}
          onClick={() => setConfirmando(true)}
        >
          <MonitorUp aria-hidden="true" />
          Publicar no painel do cliente
        </PermissionButton>
      </CardContent>

      <PublicarPainelDialog
        aberto={confirmando}
        versao={versao}
        aoFechar={() => setConfirmando(false)}
        aoConcluir={(resultado) => {
          setConfirmando(false);
          setVersao(null);
          aoPublicar(resultado);
        }}
      />
    </Card>
  );
}

function Historico({ historico }: { historico: PublicacaoPainel[] }) {
  const colunas: Array<Coluna<PublicacaoPainel>> = [
    {
      id: 'quando',
      cabecalho: 'Quando',
      ordenarPor: (item) => item.criado_em,
      celula: (item) => (
        <time dateTime={item.criado_em} title={formatarRelativo(item.criado_em)}>
          {formatarDataHora(item.criado_em)}
        </time>
      ),
    },
    {
      id: 'troca',
      cabecalho: 'Troca',
      celula: (item) => (
        <span className="inline-flex flex-wrap items-center gap-1.5 font-mono">
          <span className="text-on-surface-muted">{item.versao_anterior ?? '—'}</span>
          <ArrowRight className="size-3 text-icon" aria-label="para" />
          <Link to={`/versoes/${encodeURIComponent(item.versao)}`} className="font-bold text-primary underline-offset-4 hover:underline">
            {item.versao}
          </Link>
        </span>
      ),
    },
    { id: 'usuario', cabecalho: 'Por', ordenarPor: (item) => item.usuario, celula: (item) => item.usuario },
  ];

  return (
    <TabelaPainel titulo="Histórico de publicações" contagem={`${historico.length}`}>
      <DataTable
        itens={historico}
        colunas={colunas}
        chave={(item) => `${item.criado_em}-${item.versao}`}
        estado="pronto"
        rotulo="Histórico de publicações no painel do cliente"
        vazio={{ titulo: 'Nenhuma publicação registrada', descricao: 'As trocas de versão do painel feitas pelo Compilações aparecem aqui.' }}
      />
    </TabelaPainel>
  );
}
